import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Switch, FormControlLabel, TextField, MenuItem, Chip, Alert,
  Button, Stack, Divider, IconButton, Tooltip, CircularProgress,
} from '@mui/material';
import { ContentCopy, OpenInNew, Refresh, WifiTethering } from '@mui/icons-material';
import { toast } from 'react-toastify';
import axiosInstance from '../../../services/axiosInstance';
import { useLanguage } from '../../../contexts/LanguageContext';

interface Status {
  requested: boolean;
  active: boolean;
  expired: boolean;
  expiresAtUtc?: string | null;
  changedBy?: string | null;
  changedAtUtc?: string | null;
  firewallOpen?: boolean | null;
  firewallCheckedAtUtc?: string | null;
  serverTaskRunning: boolean;
  links: { frontend: string; backend: string }[];
}

const T = {
  ar: {
    title: 'وضع الطوارئ - الوصول من الشبكة الداخلية',
    intro: 'عند انقطاع الإنترنت الدولي: شغّل هذا الوضع ليعمل النظام من الأجهزة المتصلة بنفس شبكة الخادم (نفس الراوتر) عبر الروابط الداخلية أدناه. عند الإغلاق يعود الاتصال من الإنترنت فقط.',
    switchLabel: 'تفعيل الوصول الداخلي',
    duration: 'مدة التفعيل',
    hours: (h: number) => (h === 0 ? 'حتى أغلقه يدوياً' : `${h} ساعة`),
    expired: 'انتهت المدة',
    firewall: 'منفذ جدار الحماية', open: 'مفتوح', closed: 'مغلق', unknown: 'غير معروف',
    task: 'مهمة الخادم', taskOk: 'تعمل', taskBad: 'لا تعمل - تواصل مع الدعم',
    stConnected: 'متصل', subConnected: 'الوصول الداخلي مفتوح - يمكن استخدام الروابط الداخلية من أجهزة نفس الشبكة.',
    stConnecting: 'جاري الاتصال...', stDisconnecting: 'جاري قطع الاتصال...', subTransition: 'يُطبَّق التغيير على جدار الحماية خلال دقيقة تقريباً.',
    stDisconnected: 'غير متصل', subDisconnected: 'الوصول الداخلي مغلق - النظام يعمل من الإنترنت فقط.', stError: 'خطأ في مهمة الخادم',
    links: 'الروابط الداخلية', frontend: 'النظام (الواجهة)', backend: 'الخادم (API)',
    expires: 'ينتهي في', changedBy: 'آخر تغيير بواسطة',
    copy: 'نسخ', copied: 'تم النسخ', refresh: 'تحديث',
    note: 'الروابط تعمل فقط للأجهزة على نفس شبكة الخادم، ولا يُفتح أي منفذ في الراوتر. المنفذ يُفتح للشبكات الداخلية فقط.',
    confirmOn: 'سيتم فتح منفذ الخادم للشبكة الداخلية فقط. متابعة؟',
    saved: 'تم الحفظ', error: 'حدث خطأ',
  },
  en: {
    title: 'Emergency mode - internal network access',
    intro: 'If international internet is cut: switch this on so the system works from devices on the same network as the server (same router) through the internal links below. When switched off, access is from the internet only.',
    switchLabel: 'Enable internal access',
    duration: 'Duration',
    hours: (h: number) => (h === 0 ? 'Until I switch it off' : `${h} hours`),
    expired: 'Expired',
    firewall: 'Firewall port', open: 'Open', closed: 'Closed', unknown: 'Unknown',
    task: 'Server task', taskOk: 'Running', taskBad: 'Not running - contact support',
    stConnected: 'CONNECTED', subConnected: 'Internal access is open - the internal links can be used from devices on the same network.',
    stConnecting: 'CONNECTING...', stDisconnecting: 'DISCONNECTING...', subTransition: 'The firewall change is being applied (about one minute).',
    stDisconnected: 'DISCONNECTED', subDisconnected: 'Internal access is closed - the system works from the internet only.', stError: 'SERVER TASK ERROR',
    links: 'Internal links', frontend: 'System (frontend)', backend: 'Server (API)',
    expires: 'Expires', changedBy: 'Last changed by',
    copy: 'Copy', copied: 'Copied', refresh: 'Refresh',
    note: 'Links work only for devices on the server network; no router port is opened. The port is opened for private networks only.',
    confirmOn: 'The server port will be opened for the internal network only. Continue?',
    saved: 'Saved', error: 'Error',
  },
};

export default function EmergencyAccessTab() {
  const { language } = useLanguage();
  const t = T[language as keyof typeof T] ?? T.en;
  const [status, setStatus] = useState<Status | null>(null);
  const [hours, setHours] = useState<number>(8);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axiosInstance.get('/emergency-access/status'); setStatus(r.data); }
    catch (e: any) { toast.error(e?.response?.data?.message || t.error); }
  }, [t.error]);

  useEffect(() => { load(); const id = setInterval(load, 5000); return () => clearInterval(id); }, [load]);

  const toggle = async (enabled: boolean) => {
    if (enabled && !window.confirm(t.confirmOn)) return;
    setBusy(true);
    try { const r = await axiosInstance.post('/emergency-access', { enabled, hours: enabled ? hours : null }); setStatus(r.data); toast.success(t.saved); }
    catch (e: any) { toast.error(e?.response?.data?.message || t.error); }
    finally { setBusy(false); }
  };

  const copy = (s: string) => { navigator.clipboard?.writeText(s); toast.info(t.copied); };
  const fmt = (d?: string | null) => (d ? new Date(d).toLocaleString(language === 'ar' ? 'ar-KW' : 'en-GB') : '-');
  const fwText = status?.firewallOpen == null ? t.unknown : status.firewallOpen ? t.open : t.closed;

  return (
    <Box dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <WifiTethering color={status?.active ? 'warning' : 'disabled'} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{t.title}</Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title={t.refresh}><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t.intro}</Typography>

          {!status ? <CircularProgress size={24} /> : (
            <>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
                <FormControlLabel
                  control={<Switch checked={status.requested && !status.expired} disabled={busy} onChange={(e) => toggle(e.target.checked)} color="warning" />}
                  label={<Typography sx={{ fontWeight: 600 }}>{t.switchLabel}</Typography>}
                />
                <TextField select size="small" label={t.duration} value={hours} onChange={(e) => setHours(Number(e.target.value))}
                  disabled={busy || (status.requested && !status.expired)} sx={{ minWidth: 200 }}>
                  {[1, 4, 8, 24, 72, 0].map((h) => <MenuItem key={h} value={h}>{t.hours(h)}</MenuItem>)}
                </TextField>
              </Stack>

              {(() => {
                // connection state = what the firewall actually is + what was requested
                const fwOpen = status.firewallOpen === true;
                const state = !status.serverTaskRunning ? 'error'
                  : status.active && fwOpen ? 'connected'
                  : status.active && !fwOpen ? 'connecting'
                  : !status.active && fwOpen ? 'disconnecting' : 'disconnected';
                const S = {
                  connected:     { color: '#2e7d32', bg: 'rgba(46,125,50,0.12)',  title: t.stConnected,     sub: t.subConnected },
                  connecting:    { color: '#ed6c02', bg: 'rgba(237,108,2,0.12)',  title: t.stConnecting,    sub: t.subTransition },
                  disconnecting: { color: '#ed6c02', bg: 'rgba(237,108,2,0.12)',  title: t.stDisconnecting, sub: t.subTransition },
                  disconnected:  { color: '#d32f2f', bg: 'rgba(211,47,47,0.10)',  title: t.stDisconnected,  sub: t.subDisconnected },
                  error:         { color: '#d32f2f', bg: 'rgba(211,47,47,0.10)',  title: t.stError,         sub: t.taskBad },
                }[state];
                const pulsing = state === 'connecting' || state === 'disconnecting' || state === 'connected';
                return (
                  <Box sx={{ mb: 2, p: 2, borderRadius: 2, border: `1px solid ${S.color}`, bgcolor: S.bg, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ position: 'relative', width: 18, height: 18, flexShrink: 0 }}>
                      <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: S.color }} />
                      {pulsing && <Box sx={{ position: 'absolute', inset: -6, borderRadius: '50%', border: `2px solid ${S.color}`, opacity: 0.6,
                        animation: 'emPulse 1.6s ease-out infinite', '@keyframes emPulse': { '0%': { transform: 'scale(0.6)', opacity: 0.8 }, '100%': { transform: 'scale(1.4)', opacity: 0 } } }} />}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800, letterSpacing: 1, color: S.color, fontSize: '1.15rem', lineHeight: 1.2 }}>{S.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{S.sub}</Typography>
                      {status.active && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                        {t.expires}: {status.expiresAtUtc ? fmt(status.expiresAtUtc) : t.hours(0)} · {t.changedBy}: {status.changedBy || '-'}
                      </Typography>}
                      {status.expired && <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>{t.expired}</Typography>}
                    </Box>
                    <Tooltip title={`${t.firewall}: ${fwText} · ${t.task}: ${status.serverTaskRunning ? t.taskOk : t.taskBad}${status.firewallCheckedAtUtc ? ' · ' + fmt(status.firewallCheckedAtUtc) : ''}`}>
                      <Chip size="small" variant="outlined" label={`${t.firewall}: ${fwText}`} sx={{ borderColor: S.color, color: S.color, fontWeight: 600 }} />
                    </Tooltip>
                  </Box>
                );
              })()}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{t.links}</Typography>
              {status.links.map((l) => (
                <Stack key={l.frontend} spacing={1} sx={{ mb: 1 }}>
                  {[[t.frontend, l.frontend], [t.backend, l.backend]].map(([label, url]) => (
                    <Stack key={url} direction="row" alignItems="center" spacing={1}>
                      <Typography sx={{ minWidth: 150 }}>{label}</Typography>
                      <Typography sx={{ fontFamily: 'monospace', direction: 'ltr' }}>{url}</Typography>
                      <Tooltip title={t.copy}><IconButton size="small" onClick={() => copy(url)}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                      {url === l.frontend && <Button size="small" endIcon={<OpenInNew />} href={url} target="_blank" disabled={!status.active}>{t.frontend}</Button>}
                    </Stack>
                  ))}
                </Stack>
              ))}
              <Alert severity="warning" sx={{ mt: 2 }}>{t.note}</Alert>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
